/* eslint-disable @angular-eslint/prefer-standalone */
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, QueryParamsHandling, Router } from '@angular/router';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { DateFormatterService } from '@core/services/helpers/date-formatter.service';
import { CdiDailyAnnualizedCurrent } from '@interfaces/a-organizar/cdiDailyAnnualizedCurrent';
import { CdiDailyCurrent } from '@interfaces/a-organizar/cdiDailyCurrent';
import { CurrencyExchangeCurrent } from '@interfaces/a-organizar/currencyExchangeCurrent';
import { DerivativeDate } from '@interfaces/a-organizar/derivativeDate';
import { EconomicCalendar } from '@interfaces/a-organizar/economicCalendar';
import { CalendarDateEvent } from '@interfaces/a-organizar/events/calendarDateEvent';
import { CalendarEvent } from '@interfaces/a-organizar/events/calendarEvent';
import {
  ImplicitIpcaCurvePoint,
  ImplicitIpcaCurveResponse,
} from '@interfaces/a-organizar/implicitIpcaCurve';
import { IpcaMonthCurrent } from '@interfaces/a-organizar/ipcaMonthCurrent';
import {
  PtaxCurveCurrency,
  PtaxCurveLast60DaysResponse,
  PtaxCurvePoint,
} from '@interfaces/a-organizar/ptaxCurve';
import { Option } from '@interfaces/common/option';
import {
  CopomCalendarResponse,
  CopomEventResponse,
  ExtendedFinancialIndicatorsResponse,
  FocusExpectationResponse,
  FocusReportResponse,
  KeyIndicatorResponse,
  KeyIndicatorsDashboardResponse,
} from '@interfaces/dashboard/responses/key-indicators-dashboard.response';
import { AvailableVsContractedCreditDto } from '@interfaces/responses/bank-relationship.response';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogService } from '@services/helpers/dialog.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { faCny } from '@fortawesome/free-solid-svg-icons';
import { CompanyService } from '@services/http/company.service';
import { DashboardService } from '@services/http/dashboard.service';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { CompanySelectionService } from '@services/helpers/company-selection.service';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import { AppState } from '@src/app/state';
import { selectCompanyId } from '@src/app/state/auth/auth.selectors';
import { selectPageFilterCompanyIds } from '@src/app/state/global/global.selectors';
import Chart, { ChartConfiguration, ChartData } from 'chart.js/auto';
import { MenuItem } from 'primeng/api';
import { Popover } from 'primeng/popover';
import { Subject, catchError, of, switchMap, take, takeUntil, timer } from 'rxjs';
import { FeatureFlagStore } from '@services/feature-flags/feature-flag.store';
import {
  EARLY_SETTLEMENT_BADGE,
  earlySettlementDetailRows,
  isEarlySettledLoanEvent,
} from './utils/calendar-early-settlement.util';
import {
  DEFAULT_FIXED_DASHBOARD_INDICATORS,
  DashboardIndicatorPreferencesRepository,
} from './services/dashboard-indicator-preferences.repository';

interface CalendarMonthChangeEvent {
  month?: number;
  year?: number;
}

export interface CalendarDate {
  day: number;
  month: number;
  year: number;
}

interface FixedIndicatorOption {
  id: string;
  label: string;
}

interface CalendarPreviewEvent {
  date: string;
  dateObject: Date;
  event: CalendarEvent;
  index: number;
  title: string;
  typeLabel: string;
  currencyCode: string;
  value: number;
  detailRows: CalendarPreviewDetailRow[];
}

interface CalendarPreviewDetailRow {
  label: string;
  value: string;
}

/**
 * Dashboard clássica: a tela de antes da Home nova (cards 3786/3788), restaurada sem mudança de comportamento.
 * Abre em `/dashboard/dashboard-management` para quem não tem a flag `dashboard-home` e, para todos, em
 * `/dashboard/dashboard-classic`. Código legado mantido como estava: não expandir; sai quando a Home nova for para
 * todos e a rota clássica for aposentada.
 */
@Component({
  selector: 'app-dashboard-classic',
  standalone: false,
  templateUrl: './dashboard-classic.component.html',
  styleUrls: ['./dashboard-classic.component.scss'],
})
export class DashboardClassicComponent
  extends VaultBaseComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  faCny = faCny;

  @ViewChild(DialogComponent) private calendarEventsDialog: DialogComponent | null = null;
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: 'Cognito',
  };
  breadcrumbs: MenuItem[] = [
    {
      routerLink: '/dashboard/dashboard-management',
      label: 'Dashboard',
    },
  ];
  cdiDailyCurrent: CdiDailyCurrent | null = null;
  cdiDailyAnnualizedCurrent: CdiDailyAnnualizedCurrent | null = null;
  ipcaMonthCurrent: IpcaMonthCurrent | null = null;
  currencyExchangeCurrent: CurrencyExchangeCurrent[] = [];
  sofrCurrent: CdiDailyCurrent | null = null;
  keyIndicatorsDashboard: KeyIndicatorsDashboardResponse | null = null;
  isLoadingKeyIndicators = true;
  ptaxCurveData: PtaxCurveCurrency[] = [];
  isLoadingPtaxCurve = true;
  implicitIpcaCurveData: ImplicitIpcaCurveResponse | null = null;
  isLoadingImplicitIpcaCurve = true;
  fixedIndicatorIds: string[] = [...DEFAULT_FIXED_DASHBOARD_INDICATORS];
  fixedIndicatorSearchTerm = '';
  isEditingIndicators = false;
  swapTargetIndex: number | null = null;

  @ViewChild('indicatorPicker') private indicatorPicker: Popover | null = null;

  // Loading states for financial index cards
  isLoadingCdi = true;
  isLoadingIpca = true;
  isLoadingCurrencies = true;
  isLoadingSofr = true;

  // Fixed currency structure to always show cards (even while loading)
  currencyKeys = ['USD', 'EUR', 'CNY', 'CHF', 'JPY'];
  readonly showDerivativeBarChart = false;
  readonly showEconomicCalendar = false;
  /** Selo da parcela liquidada antes do vencimento na tabela do calendário (card 3201). */
  readonly earlySettlementBadge = EARLY_SETTLEMENT_BADGE;
  derivativeBarChart: Chart | null = null;
  derivativeBarHorizontalChart: Chart | null = null;
  derivativeLineChart: Chart | null = null;
  derivativeSemiDoughnutChart: Chart | null = null;
  availableVsContractedCreditChart: Chart<'doughnut', number[], unknown> | null = null;
  currentDate: Date = new Date();
  economicCalendarList: EconomicCalendar[] = [];
  headers: string[] = [];
  columns: string[] = [];
  calendarEvents: CalendarDateEvent[] = [];
  private destroy$ = new Subject<void>();
  companiesOptions: Option[] = [];
  selectedCompanyIds: string[] = [];
  selectedCompanyId: string | null = null;
  // Flag temporária de rollout do cenário de economia. Remover quando a feature
  // ficar 1-2 semanas com enabledForAll=true sem incidentes.
  dashboardEconomyScenarioEnabled = false;
  availableVsContractedCredit: AvailableVsContractedCreditDto | null = null;
  availableVsContractedCreditChartData: ChartData<'doughnut'> | null = null;
  highlightedDatesSet = new Set<string>();
  private pendingModalParams: { modal: string; date: string } | null = null;
  private isOpeningModalFromClick = false;
  private readonly featureFlagStore = inject(FeatureFlagStore);
  private readonly companySelection = inject(CompanySelectionService);

  constructor(
    translateService: TranslateService,
    private dashboardService: DashboardService,
    private utilsService: UtilsService,
    public dateFormatter: DateFormatterService,
    private confirmDialogService: ConfirmDialogService,
    private companyService: CompanyService,
    protected override store: Store<AppState>,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private indicatorPreferencesRepository: DashboardIndicatorPreferencesRepository,
    private toastHelper: ToastHelperService,
    private permissionsService: PermissionsService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    super(translateService);
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * REGRA DE NEGÓCIO: a seção "Cenário de Economia do Cliente" (cards de Economia
   * Real/Prevista + gráfico Performance da Dívida) NÃO aparece com o escopo
   * "Todas empresas" — exige pelo menos uma empresa explicitamente selecionada
   * (`selectedCompanyIds`). Motivo: sem recorte de empresa os valores consolidariam
   * o tenant inteiro, o que não é o objetivo do cenário (leitura por cliente).
   * Também exige a feature flag `dashboard-economy-scenario` habilitada (rollout).
   */
  get shouldShowDashboardEconomyScenario(): boolean {
    return this.dashboardEconomyScenarioEnabled && this.selectedCompanyIds.length > 0;
  }

  isCompanyIdUser = false;

  /** Sem poder listar empresas, o filtro de empresa do cabeçalho nem chega a ser montado. */
  readonly companyListPermission = Permissions.Company.LIST;

  ngOnInit(): void {
    this.headers = [
      this.t(this.tk.General.Time),
      this.t(this.tk.Management.NdfCurrency),
      this.t(this.tk.General.Event),
      this.t(this.tk.General.Real),
      this.t(this.tk.General.Previous),
      this.t(this.tk.General.Forecast),
    ];
    this.columns = ['time', 'currency', 'event', 'actual', 'previous', 'forecast'];
    this.getCompanyOptions();
    this.initializePageFilterCompanySelection();
    this.store
      .select(selectCompanyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(companyId => {
        if (companyId) {
          const changed = this.applyDashboardCompanyIds([companyId]);
          this.isCompanyIdUser = true;
          if (changed) {
            this.reloadCompanyScopedBlocks();
          }
        }
      });
    this.getCdiDailyCurrent();
    this.getCdiDailyAnnualizedCurrent();
    this.getIpcaMonthCurrent();
    this.startCurrencyPolling();
    this.getSofrCurrent();
    this.fixedIndicatorIds = this.indicatorPreferencesRepository.loadFixedIndicatorIds();
    this.getKeyIndicatorsDashboard();
    this.getPtaxCurveLastAvailable();
    this.getImplicitIpcaCurveLastAvailable();
    this.loadDashboardEconomyScenarioToggle();
    if (this.showEconomicCalendar) {
      this.getEconomicCalendar();
    }
    this.getAvailableVsContractedCredit();

    const today = this.currentDate;
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    this.getCalendarEvents(currentMonth, currentYear);

    // Escutar mudanças na URL para controlar o modal
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.checkAndOpenModalFromUrl(params);
    });
  }

  onDashboardManagementCompanySelectedMultiple(selectedOptions: Option[] | null): void {
    this.applyDashboardCompanyIds((selectedOptions ?? []).map(option => String(option.value)));
    this.handleCompanyChange();
  }

  onDashboardManagementCompanySelected(option: Option | null): void {
    this.applyDashboardCompanyIds(option?.value ? [String(option.value)] : []);
    this.handleCompanyChange();
  }

  private initializePageFilterCompanySelection(): void {
    // Prioridade (card 3571): URL explicita (`companyId=all` ou CSV) > estado global. Com `all` antes de a lista
    // de empresas ser conhecida, comeca sem filtro (todas) e o app-select-company emite a selecao completa ao carregar.
    const params = this.route.snapshot.queryParams;
    const queryCompanyIds = this.companySelection.resolveUrlCompanyIds(params);

    if (queryCompanyIds.length > 0 || this.companySelection.isAllRequested(params)) {
      this.applyDashboardCompanyIds(queryCompanyIds);
      return;
    }

    this.store
      .select(selectPageFilterCompanyIds)
      .pipe(take(1))
      .subscribe(companyIds => {
        const changed = this.applyDashboardCompanyIds(companyIds);
        if (changed) {
          this.reloadCompanyScopedBlocks();
        }
      });
  }

  private applyDashboardCompanyIds(companyIds: string[] | null | undefined): boolean {
    const normalizedCompanyIds = this.normalizeCompanyIds(companyIds);
    const nextCompanyId = normalizedCompanyIds.length === 1 ? normalizedCompanyIds[0] : null;
    const changed =
      this.selectedCompanyId !== nextCompanyId ||
      this.selectedCompanyIds.join('|') !== normalizedCompanyIds.join('|');

    this.selectedCompanyIds = normalizedCompanyIds;
    this.selectedCompanyId = nextCompanyId;
    this.cdr.markForCheck();
    return changed;
  }

  private normalizeCompanyIds(companyIds: string[] | null | undefined): string[] {
    return [...new Set((companyIds ?? []).map(id => String(id).trim()).filter(Boolean))].sort();
  }

  /**
   * Disponibilidade do cenário de economia para o usuário atual, mantida em dia pelo FeatureFlagStore
   * (reconsulta ao voltar para a aba e ao navegar; liberou, aparece sem relogar, card 3323).
   * O backend protege o endpoint real; esta consulta só evita renderizar a UI para quem não está no rollout.
   */
  private loadDashboardEconomyScenarioToggle(): void {
    this.featureFlagStore
      .watch$('dashboard-economy-scenario')
      .pipe(takeUntil(this.destroy$))
      .subscribe(enabled => {
        this.dashboardEconomyScenarioEnabled = enabled;
        this.cdr.markForCheck();
      });
  }

  get fixedIndicatorOptions(): FixedIndicatorOption[] {
    const baseOptions: FixedIndicatorOption[] = [
      { id: 'cdi', label: 'CDI' },
      { id: 'ipca', label: 'IPCA (Mês)' },
      { id: 'currency:USD', label: 'USD/BRL' },
      { id: 'currency:EUR', label: 'EUR/BRL' },
      { id: 'currency:CNY', label: 'CNY/BRL' },
      { id: 'currency:CHF', label: 'CHF/BRL' },
      { id: 'currency:JPY', label: 'JPY/BRL' },
      { id: 'ptax:USD', label: 'PTAX USD Venda' },
      { id: 'ptax-buy:USD', label: 'PTAX USD Compra' },
      { id: 'ptax:EUR', label: 'PTAX EUR Venda' },
      { id: 'ptax-buy:EUR', label: 'PTAX EUR Compra' },
      { id: 'ptax:CHF', label: 'PTAX CHF Venda' },
      { id: 'ptax-buy:CHF', label: 'PTAX CHF Compra' },
      { id: 'ptax:JPY', label: 'PTAX JPY Venda' },
      { id: 'ptax-buy:JPY', label: 'PTAX JPY Compra' },
      { id: 'implicit-ipca', label: 'IPCA Implícito' },
      { id: 'sofr', label: 'SOFR' },
    ];

    const keyIndicatorOptions =
      this.getKeyIndicatorsForFixedSelector().map(indicator => ({
        id: this.toKeyIndicatorFixedId(indicator.code),
        label: this.getKeyIndicatorDisplayName(indicator),
      })) ?? [];

    return [...baseOptions, ...keyIndicatorOptions];
  }

  get availableFixedIndicatorOptions(): FixedIndicatorOption[] {
    const search = this.normalizeFixedIndicatorSearch(this.fixedIndicatorSearchTerm);

    return this.fixedIndicatorOptions
      .filter(option => !this.isFixedIndicatorSelected(option.id))
      .filter(option => {
        if (!search) {
          return true;
        }

        return this.normalizeFixedIndicatorSearch(`${option.id} ${option.label}`).includes(search);
      })
      .sort((a, b) => Number(a.id.startsWith('ptax')) - Number(b.id.startsWith('ptax')));
  }

  get availableFixedIndicatorGroups(): { label: string; options: FixedIndicatorOption[] }[] {
    const groupOrder = ['Moedas', 'Juros e política monetária', 'Inflação', 'Atividade e outros'];
    const grouped = new Map<string, FixedIndicatorOption[]>();

    for (const option of this.availableFixedIndicatorOptions) {
      const group = this.getFixedIndicatorGroup(option.id);
      const current = grouped.get(group) ?? [];
      current.push(option);
      grouped.set(group, current);
    }

    return groupOrder
      .filter(label => grouped.has(label))
      .map(label => ({ label, options: grouped.get(label) ?? [] }));
  }

  private getFixedIndicatorGroup(indicatorId: string): string {
    if (indicatorId.startsWith('currency:') || indicatorId.startsWith('ptax')) {
      return 'Moedas';
    }

    if (indicatorId === 'cdi' || indicatorId === 'sofr' || indicatorId.includes('selic')) {
      return 'Juros e política monetária';
    }

    if (indicatorId.includes('ipca') || indicatorId.includes('igpm')) {
      return 'Inflação';
    }

    return 'Atividade e outros';
  }

  get canSaveFixedIndicators(): boolean {
    return this.fixedIndicatorIds.length >= 4 && this.fixedIndicatorIds.length <= 8;
  }

  get fixedIndicatorsLimitLabel(): string {
    if (this.fixedIndicatorIds.length >= 8) {
      return 'Limite de 8 atingido';
    }

    return `${this.fixedIndicatorIds.length}/8 selecionados`;
  }

  isFixedIndicatorSelected(indicatorId: string): boolean {
    return this.fixedIndicatorIds.includes(indicatorId);
  }

  startEditingIndicators(): void {
    this.isEditingIndicators = true;
  }

  finishEditingIndicators(): void {
    this.isEditingIndicators = false;
    this.swapTargetIndex = null;
    this.clearFixedIndicatorSearchTerm();
    this.indicatorPicker?.hide();
    this.toastHelper.showToastSuccess(
      'Indicadores atualizados',
      'Sua personalização da dashboard foi salva.'
    );
  }

  get indicatorPickerTitle(): string {
    return this.swapTargetIndex !== null ? 'Trocar indicador' : 'Adicionar indicador';
  }

  openAddIndicatorPicker(event: Event): void {
    this.swapTargetIndex = null;
    this.clearFixedIndicatorSearchTerm();
    this.indicatorPicker?.toggle(event);
  }

  openSwapIndicatorPicker(event: Event, index: number): void {
    this.swapTargetIndex = index;
    this.clearFixedIndicatorSearchTerm();
    this.indicatorPicker?.toggle(event);
  }

  selectIndicatorFromPicker(indicatorId: string): void {
    if (this.swapTargetIndex !== null) {
      this.swapFixedIndicator(this.swapTargetIndex, indicatorId);
      return;
    }

    this.addFixedIndicator(indicatorId);
  }

  swapFixedIndicator(index: number, indicatorId: string): void {
    if (
      index < 0 ||
      index >= this.fixedIndicatorIds.length ||
      this.isFixedIndicatorSelected(indicatorId)
    ) {
      return;
    }

    const updatedIds = [...this.fixedIndicatorIds];
    updatedIds[index] = indicatorId;
    this.fixedIndicatorIds = updatedIds;
    this.saveFixedIndicatorPreferences();
    this.clearFixedIndicatorSearchTerm();
    this.swapTargetIndex = null;
    this.indicatorPicker?.hide();
  }

  addFixedIndicator(indicatorId: string): void {
    if (this.isFixedIndicatorSelected(indicatorId) || this.fixedIndicatorIds.length >= 8) {
      return;
    }

    this.fixedIndicatorIds = [...this.fixedIndicatorIds, indicatorId];
    this.saveFixedIndicatorPreferences();
    this.clearFixedIndicatorSearchTerm();

    if (this.fixedIndicatorIds.length >= 8) {
      this.indicatorPicker?.hide();
    }
  }

  removeFixedIndicator(indicatorId: string): void {
    if (!this.isFixedIndicatorSelected(indicatorId) || this.fixedIndicatorIds.length <= 4) {
      return;
    }

    this.fixedIndicatorIds = this.fixedIndicatorIds.filter(id => id !== indicatorId);
    this.saveFixedIndicatorPreferences();
  }

  dropFixedIndicator(event: CdkDragDrop<string[]>): void {
    if (event.previousContainer !== event.container || event.previousIndex === event.currentIndex) {
      return;
    }

    const updatedIds = [...this.fixedIndicatorIds];
    moveItemInArray(updatedIds, event.previousIndex, event.currentIndex);
    this.fixedIndicatorIds = updatedIds;
    this.saveFixedIndicatorPreferences();
  }

  resetFixedIndicators(): void {
    this.fixedIndicatorIds = [...DEFAULT_FIXED_DASHBOARD_INDICATORS];
    this.saveFixedIndicatorPreferences();
  }

  refreshDashboardData(): void {
    this.getCdiDailyCurrent();
    this.getCdiDailyAnnualizedCurrent();
    this.getIpcaMonthCurrent();
    this.refreshCurrencyExchangeCurrent();
    this.getSofrCurrent();
    this.getKeyIndicatorsDashboard(true);
    this.getPtaxCurveLastAvailable();
    this.getImplicitIpcaCurveLastAvailable();

    const currentMonth = this.currentDate.getMonth();
    const currentYear = this.currentDate.getFullYear();
    this.fetchCalendarEventsForTwoMonths(currentMonth, currentYear);
    this.getAvailableVsContractedCredit();
  }

  updateFixedIndicatorSearchTerm(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.fixedIndicatorSearchTerm = input.value;
  }

  clearFixedIndicatorSearchTerm(): void {
    this.fixedIndicatorSearchTerm = '';
  }

  getFixedIndicatorLabel(indicatorId: string): string {
    return (
      this.fixedIndicatorOptions.find(option => option.id === indicatorId)?.label ?? indicatorId
    );
  }

  get fixedKeyIndicatorCodes(): string[] {
    return this.fixedIndicatorIds
      .filter(indicatorId => indicatorId.startsWith('key:'))
      .map(indicatorId => indicatorId.replace('key:', ''));
  }

  get additionalKeyIndicators(): KeyIndicatorResponse[] {
    const indicators: KeyIndicatorResponse[] = [];
    const addWhenNotFixed = (fixedId: string, indicator: KeyIndicatorResponse | null) => {
      if (!this.fixedIndicatorIds.includes(fixedId) && indicator) {
        indicators.push(indicator);
      }
    };

    addWhenNotFixed(
      'cdi',
      this.cdiDailyAnnualizedCurrent
        ? this.createLocalKeyIndicator({
            code: 'cdi',
            name: 'CDI',
            description: 'Taxa CDI anualizada vigente.',
            value: this.cdiDailyAnnualizedCurrent.annualized,
            formattedValue: this.formatLocalPercent(this.cdiDailyAnnualizedCurrent.annualized),
            unit: '% a.a.',
            period: this.formatLocalDateLabel(this.cdiDailyAnnualizedCurrent.date),
            source: 'Banco Central do Brasil/SGS',
            lastSuccessfulUpdateAtGmt: this.cdiDailyAnnualizedCurrent.date,
          })
        : null
    );

    addWhenNotFixed(
      'ipca',
      this.ipcaMonthCurrent
        ? this.createLocalKeyIndicator({
            code: 'ipca-month',
            name: 'IPCA (Mês)',
            description: 'Variação mensal mais recente do IPCA.',
            value: this.ipcaMonthCurrent.value,
            formattedValue: this.formatLocalPercent(this.ipcaMonthCurrent.value),
            unit: '%',
            period: this.formatLocalDateLabel(this.ipcaMonthCurrent.date),
            source: 'IBGE',
            lastSuccessfulUpdateAtGmt: this.ipcaMonthCurrent.date,
          })
        : null
    );

    this.currencyKeys.forEach(currencyCode => {
      const currency = this.getCurrencyByCode(currencyCode);
      addWhenNotFixed(
        `currency:${currencyCode}`,
        currency
          ? this.createLocalKeyIndicator({
              code: `currency-${currencyCode.toLowerCase()}`,
              name: `${currencyCode}/BRL`,
              description: `Cotação atual de ${currencyCode} contra BRL.`,
              value: currency.currentQuote,
              formattedValue: this.formatLocalDecimal(currency.currentQuote, 4),
              unit: 'BRL',
              period: this.formatLocalDateLabel(currency.collectedAt || currency.lastUpdated),
              source: currency.sourceName || 'Banco Central do Brasil',
              lastSuccessfulUpdateAtGmt: currency.collectedAt || currency.lastUpdated || null,
            })
          : null
      );
    });

    ['USD', 'EUR', 'CHF', 'JPY'].forEach(currencyCode => {
      const ptaxPoint = this.getLatestPtaxPoint(currencyCode);
      (['sale', 'purchase'] as const).forEach(quoteType => {
        addWhenNotFixed(
          this.getPtaxFixedId(currencyCode, quoteType),
          ptaxPoint
            ? this.createLocalKeyIndicator({
                code: `ptax-${currencyCode.toLowerCase()}-${quoteType}`,
                name: `PTAX ${currencyCode} ${this.getPtaxQuoteTypeLabel(quoteType)}`,
                description: `Última taxa de ${this.getPtaxQuoteTypeLabel(quoteType).toLowerCase()} PTAX disponível para ${currencyCode}.`,
                value: this.getPtaxQuoteValue(ptaxPoint, quoteType),
                formattedValue: this.formatLocalDecimal(
                  this.getPtaxQuoteValue(ptaxPoint, quoteType),
                  4
                ),
                unit: 'BRL',
                period: this.formatLocalDateLabel(ptaxPoint.quoteDate),
                source: 'Banco Central do Brasil (PTAX)',
                lastSuccessfulUpdateAtGmt: this.getPtaxLastUpdate(currencyCode),
              })
            : null
        );
      });
    });

    const implicitIpcaPoint = this.getLatestImplicitIpcaPoint();
    addWhenNotFixed(
      'implicit-ipca',
      implicitIpcaPoint
        ? this.createLocalKeyIndicator({
            code: 'implicit-ipca',
            name: 'IPCA Implícito',
            description: 'IPCA implícito calculado a partir das curvas DI1 e DAP.',
            value: implicitIpcaPoint.implicitIpcaRate,
            formattedValue: this.formatLocalPercent(implicitIpcaPoint.implicitIpcaRate),
            unit: '%',
            period: this.formatLocalDateLabel(implicitIpcaPoint.maturityDate),
            source: 'Cálculo Legatus sobre curvas B3 DI1 e DAP',
            lastSuccessfulUpdateAtGmt: this.implicitIpcaCurveData?.lastUpdate ?? null,
          })
        : null
    );

    addWhenNotFixed(
      'sofr',
      this.sofrCurrent
        ? this.createLocalKeyIndicator({
            code: 'sofr',
            name: 'SOFR',
            description: 'Secured Overnight Financing Rate.',
            value: this.sofrCurrent.value,
            formattedValue: this.formatLocalPercent(this.sofrCurrent.value),
            unit: '%',
            period: this.formatLocalDateLabel(this.sofrCurrent.date),
            source: 'Federal Reserve Bank of New York',
            lastSuccessfulUpdateAtGmt: this.sofrCurrent.date,
          })
        : null
    );

    return indicators;
  }

  getKeyIndicatorByFixedId(indicatorId: string): KeyIndicatorResponse | null {
    if (!indicatorId.startsWith('key:')) {
      return null;
    }

    const code = indicatorId.replace('key:', '');
    return (
      this.getKeyIndicatorsForFixedSelector().find(indicator => indicator.code === code) ?? null
    );
  }

  getKeyIndicatorDisplayName(indicator: KeyIndicatorResponse): string {
    const displayNames: Record<string, string> = {
      'ipca-12m': 'Inflação (IPCA) - 12M',
      'selic-target': 'Taxa Selic',
      'copom-last-minutes': 'Última ata do Copom',
      'copom-last-statement': 'Último comunicado do Copom',
      'copom-next-meeting': 'Próxima reunião do Copom',
      'copom-next-publication': 'Próxima publicação do Copom',
      'focus-expectation-ipca': 'Focus IPCA',
      'focus-expectation-cambio': 'Focus Câmbio',
      'focus-expectation-pib': 'Focus PIB',
      'focus-expectation-selic': 'Focus Selic',
      'focus-last-release': 'Última divulgação Focus',
      'focus-next-release': 'Próxima divulgação Focus',
      'gdp-brazil-4q': 'PIB (Brasil)',
      'industrial-production-monthly-sa': 'Produção Industrial',
      'unemployment-rate': 'Desemprego',
      'focus-ipca-current-year': 'Focus IPCA esperado',
      'focus-selic-current-year': 'Focus Selic esperado',
      'igpm-12m': 'IGP-M - acumulado 12M',
      'ibc-br-monthly-sa': 'IBC-Br',
      'primary-result-public-sector-12m': 'Resultado primário',
      'gross-debt-general-government': 'Dívida bruta do governo geral',
      'average-real-monthly-income': 'Rendimento médio mensal real',
      'real-monthly-wage-bill': 'Massa de rendimento mensal real',
      'retail-sales-volume-monthly-sa': 'Vendas no varejo',
      'credit-delinquency-free-resources': 'Inadimplência da carteira de crédito',
    };

    return displayNames[indicator.code] ?? indicator.name;
  }

  getKeyIndicatorIconClass(indicator: KeyIndicatorResponse): string {
    if (indicator.code.startsWith('focus-exchange-rate-')) {
      return 'pi pi-dollar';
    }

    const iconClasses: Record<string, string> = {
      'ipca-12m': 'pi pi-chart-line',
      'selic-target': 'pi pi-gauge',
      'copom-last-minutes': 'pi pi-file',
      'copom-last-statement': 'pi pi-bell',
      'copom-next-meeting': 'pi pi-calendar',
      'copom-next-publication': 'pi pi-calendar-plus',
      'focus-expectation-ipca': 'pi pi-bullseye',
      'focus-expectation-cambio': 'pi pi-dollar',
      'focus-expectation-pib': 'pi pi-chart-line',
      'focus-expectation-selic': 'pi pi-compass',
      'focus-last-release': 'pi pi-calendar',
      'focus-next-release': 'pi pi-calendar',
      'gdp-brazil-4q': 'pi pi-file',
      'industrial-production-monthly-sa': 'pi pi-chart-bar',
      'unemployment-rate': 'pi pi-briefcase',
      'focus-ipca-current-year': 'pi pi-bullseye',
      'focus-selic-current-year': 'pi pi-bullseye',
      'igpm-12m': 'pi pi-percentage',
      'ibc-br-monthly-sa': 'pi pi-wave-pulse',
      'primary-result-public-sector-12m': 'pi pi-building-columns',
      'gross-debt-general-government': 'pi pi-wallet',
      'average-real-monthly-income': 'pi pi-money-bill',
      'real-monthly-wage-bill': 'pi pi-users',
      'retail-sales-volume-monthly-sa': 'pi pi-shopping-cart',
      'credit-delinquency-free-resources': 'pi pi-exclamation-circle',
    };

    return iconClasses[indicator.code] ?? 'pi pi-chart-scatter';
  }

  getKeyIndicatorIconBoxClass(indicator: KeyIndicatorResponse): string {
    const iconBoxClasses: Record<string, string> = {
      'selic-target': 'bg-icon-green',
      'ipca-12m': 'bg-icon-green',
    };

    return iconBoxClasses[indicator.code] ?? 'bg-icon-blue';
  }

  getKeyIndicatorPrimaryMetadata(
    indicator: KeyIndicatorResponse
  ): { label: string; value: string } | null {
    return (
      indicator.metadata?.find(item => item.label === 'Divulgado em') ??
      indicator.metadata?.find(item => item.label === 'Próxima divulgação') ??
      indicator.metadata?.find(item => item.label === 'Data da pesquisa') ??
      null
    );
  }

  getKeyIndicatorBadgeClass(indicator: KeyIndicatorResponse): string {
    const value = indicator.value ?? 0;
    if (value > 0) return 'badge-success';
    if (value < 0) return 'badge-danger';
    return 'badge-neutral';
  }

  formatHeaderUpdateTime(value: string | null | undefined): string {
    if (!value) return '';

    const normalizedValue = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`;
    const date = new Date(normalizedValue);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo',
      });
    }

    return value;
  }

  getLatestPtaxPoint(currencyCode: string): PtaxCurvePoint | null {
    const currency = this.ptaxCurveData.find(
      item => item.currencyCode.toUpperCase() === currencyCode.toUpperCase()
    );

    if (!currency?.points?.length) {
      return null;
    }

    return [...currency.points].sort((a, b) =>
      (b.quoteDate ?? '').localeCompare(a.quoteDate ?? '')
    )[0];
  }

  getPtaxLastUpdate(currencyCode: string): string | null {
    return (
      this.ptaxCurveData.find(
        item => item.currencyCode.toUpperCase() === currencyCode.toUpperCase()
      )?.lastUpdate ?? null
    );
  }

  getPtaxQuoteValue(ptaxPoint: PtaxCurvePoint, quoteType: 'sale' | 'purchase'): number {
    return quoteType === 'purchase' ? ptaxPoint.purchaseRate : ptaxPoint.saleRate;
  }

  getPtaxQuoteTypeLabel(quoteType: 'sale' | 'purchase'): string {
    return quoteType === 'purchase' ? 'Compra' : 'Venda';
  }

  getLatestImplicitIpcaPoint(): ImplicitIpcaCurvePoint | null {
    const points = this.implicitIpcaCurveData?.points || [];
    if (!points.length) {
      return null;
    }

    return [...points].sort((a, b) =>
      (b.maturityDate ?? '').localeCompare(a.maturityDate ?? '')
    )[0];
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;
    // Aguardar um pequeno delay para garantir que os elementos estejam no DOM
    // e evitar problemas de change detection
    setTimeout(() => {
      try {
        if (this.showDerivativeBarChart) {
          this.initBarChart();
        }
        // Gráficos desabilitados temporariamente para evitar warnings
        // this.initHorizontalBarChart();
        // this.initLineChart();
        // this.initSemiDoughnutChart();
      } catch (error) {
        console.warn('Error initializing charts:', error);
      }
    }, 100);
  }

  handleCalendarDayClick(event: MouseEvent) {
    // Event handler for calendar day click
  }

  /**
   * Helper para tentar encontrar um elemento no DOM com retry
   */
  private getElementWithRetry(
    elementId: string,
    maxRetries = 5,
    delay = 100
  ): Promise<HTMLElement | null> {
    if (!this.isBrowser) return Promise.resolve(null);
    return new Promise(resolve => {
      let attempts = 0;
      const tryGetElement = () => {
        attempts++;
        const element = document.getElementById(elementId);
        if (element) {
          resolve(element);
        } else if (attempts < maxRetries) {
          setTimeout(tryGetElement, delay);
        } else {
          resolve(null);
        }
      };
      tryGetElement();
    });
  }
  initSemiDoughnutChart() {
    // Gráfico não presente no HTML atual, desativado para evitar warnings
  }

  initHorizontalBarChart(companyId: string | null = null) {
    // Gráfico não presente no HTML atual, desativado para evitar warnings
  }

  initLineChart(companyId: string | null = null) {
    // Gráfico não presente no HTML atual, desativado para evitar warnings
  }

  initBarChart(companyId: string | null = null) {
    if (!this.isBrowser) return;
    this.dashboardService
      .getDashboardDerivativeMonths(12, companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const labels = (response || []).map((item: DerivativeDate) =>
            this.translateMonthToPortuguese(item.month, item.year)
          );
          const data = (response || []).map((item: DerivativeDate) => item.value);
          const elementId = 'derivative-bar-chart';
          const ctx = document.getElementById(elementId) as HTMLCanvasElement;

          if (!ctx) {
            setTimeout(() => {
              const retryCtx = document.getElementById(elementId) as HTMLCanvasElement;
              if (retryCtx) {
                this.createBarChart(retryCtx, labels, data);
              }
            }, 200);
            return;
          }

          this.createBarChart(ctx, labels, data);
        },
        error: (error: any) => {
          console.warn('Dashboard API error (handled):', error?.message || error);
        },
      });
  }

  private createBarChart(ctx: HTMLCanvasElement, labels: string[], data: number[]): void {
    if (this.derivativeBarChart) {
      try {
        this.derivativeBarChart.destroy();
      } catch (error) {}
      this.derivativeBarChart = null;
    }

    try {
      this.derivativeBarChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: '',
              data,
              backgroundColor: context => {
                const index = context.dataIndex;
                const colors = ['#020a30', '#020a30', '#020a30'];
                return colors[index % colors.length];
              },
              borderColor: 'transparent',
              borderWidth: 0,
              borderRadius: {
                topLeft: 4,
                topRight: 4,
                bottomLeft: 4,
                bottomRight: 4,
              },
              barPercentage: 0.5,
              categoryPercentage: 0.8,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              grid: { display: false },
              border: { display: false },
              ticks: {
                font: { size: 12, family: "'Inter', sans-serif" },
                color: '#6C757D',
              },
            },
            x: {
              grid: { display: false },
              border: { display: false },
              ticks: {
                font: { size: 12, family: "'Inter', sans-serif" },
                color: '#6C757D',
              },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
          },
          layout: {
            padding: { left: 10, right: 10, top: 30, bottom: 10 },
          },
        },
        plugins: [
          {
            id: 'barValueLabels',
            afterDatasetsDraw(chart) {
              const { ctx } = chart;
              chart.data.datasets.forEach((dataset, datasetIndex) => {
                const meta = chart.getDatasetMeta(datasetIndex);
                if (!meta.hidden) {
                  meta.data.forEach((element, index) => {
                    const value = dataset.data[index] as number;
                    const { x, y } = element;
                    ctx.textAlign = 'center';
                    ctx.fillText(value.toFixed(0) + 'Mi', x, y - 5);
                  });
                }
              });
            },
          },
        ],
      });
    } catch (error) {}
  }

  private createLineChart(ctx: HTMLCanvasElement, labels: string[], data: number[]): void {
    // Método mantido para compatibilidade se reativado, mas atualmente não usado
  }

  getCdiDailyCurrent(): void {
    this.isLoadingCdi = true;
    this.dashboardService
      .getDashboardCdiDailyCurrent()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.cdiDailyCurrent = response;
          this.isLoadingCdi = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.warn('CDI Daily API error:', error?.message || error);
          this.cdiDailyCurrent = null;
          this.isLoadingCdi = false;
          this.cdr.markForCheck();
        },
      });
  }

  getCdiDailyAnnualizedCurrent() {
    this.isLoadingCdi = true;
    this.dashboardService
      .getDashboardCdiDailyAnnualizedCurrent()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.cdiDailyAnnualizedCurrent = response;
          this.isLoadingCdi = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.warn('CDI Annualized API error:', error?.message || error);
          this.cdiDailyAnnualizedCurrent = null;
          this.isLoadingCdi = false;
          this.cdr.markForCheck();
        },
      });
  }

  getIpcaMonthCurrent() {
    this.isLoadingIpca = true;
    this.dashboardService
      .getDashboardIpcaMonthCurrent()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.ipcaMonthCurrent = response;
          this.isLoadingIpca = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.warn('Dashboard API error (handled):', error?.message || error);
          this.isLoadingIpca = false;
          this.cdr.markForCheck();
        },
      });
  }

  getKeyIndicatorsDashboard(forceUpdate = false): void {
    this.isLoadingKeyIndicators = true;
    this.dashboardService
      .getDashboardKeyIndicators(forceUpdate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.keyIndicatorsDashboard = response;
          this.isLoadingKeyIndicators = false;
          this.ensureFixedIndicatorsExist();
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.warn('Key indicators API error:', error?.message || error);
          this.keyIndicatorsDashboard = null;
          this.isLoadingKeyIndicators = false;
          this.ensureFixedIndicatorsExist();
          this.cdr.markForCheck();
        },
      });
  }

  getPtaxCurveLastAvailable(): void {
    this.isLoadingPtaxCurve = true;
    this.dashboardService
      .getPtaxCurveLast60Days()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const ptaxResponse = response as PtaxCurveCurrency[] | PtaxCurveLast60DaysResponse | null;
          this.ptaxCurveData = Array.isArray(ptaxResponse)
            ? ptaxResponse
            : ptaxResponse?.data || [];
          this.isLoadingPtaxCurve = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.warn('PTAX curve API error:', error?.message || error);
          this.ptaxCurveData = [];
          this.isLoadingPtaxCurve = false;
          this.cdr.markForCheck();
        },
      });
  }

  getImplicitIpcaCurveLastAvailable(): void {
    this.isLoadingImplicitIpcaCurve = true;
    this.dashboardService
      .getImplicitIpcaCurve()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.implicitIpcaCurveData = response;
          this.isLoadingImplicitIpcaCurve = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.warn('Implicit IPCA curve API error:', error?.message || error);
          this.implicitIpcaCurveData = null;
          this.isLoadingImplicitIpcaCurve = false;
          this.cdr.markForCheck();
        },
      });
  }

  private startCurrencyPolling(): void {
    this.isLoadingCurrencies = true;
    timer(0, 30_000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() =>
          this.dashboardService.getDashboardCurrencyExchangeCurrent().pipe(
            catchError((error: any) => {
              console.warn('Dashboard API error (handled):', error?.message || error);
              return of<CurrencyExchangeCurrent[]>([]);
            })
          )
        )
      )
      .subscribe({
        next: response => {
          this.currencyExchangeCurrent = response || [];
          this.isLoadingCurrencies = false;
          this.cdr.markForCheck();
        },
      });
  }

  private refreshCurrencyExchangeCurrent(): void {
    this.isLoadingCurrencies = true;
    this.dashboardService
      .getDashboardCurrencyExchangeCurrent()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.currencyExchangeCurrent = response || [];
          this.isLoadingCurrencies = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.warn('Dashboard API error (handled):', error?.message || error);
          this.isLoadingCurrencies = false;
          this.cdr.markForCheck();
        },
      });
  }

  getSofrCurrent() {
    this.isLoadingSofr = true;
    this.dashboardService
      .getDashboardSofrCurrent()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.sofrCurrent = response;
          this.isLoadingSofr = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.warn('SOFR API error:', error?.message || error);
          this.sofrCurrent = null;
          this.isLoadingSofr = false;
          this.cdr.markForCheck();
        },
      });
  }

  getEconomicCalendar() {
    if (!this.isBrowser) return;

    // Define datas: hoje e próximos 7 dias
    const today = new Date();
    const endDate = new Date();
    endDate.setDate(today.getDate() + 7);

    const startDateStr = this.formatDate(today);
    const endDateStr = this.formatDate(endDate);

    this.dashboardService
      .getDashboardEconomicCalendar(startDateStr, endDateStr)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          this.economicCalendarList = data || [];
          this.economicCalendarList.forEach(item => {
            if (item.flagImageUrl) {
              item.currency = `<img src="${item.flagImageUrl}" class="country-flag" /> ${item.currency}`;
            }
          });
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.warn('Economic Calendar API error:', error?.message || error);
          this.economicCalendarList = [];
          this.cdr.markForCheck();
        },
      });
  }

  getCalendarEvents(month: number, year: number) {
    this.fetchCalendarEventsForTwoMonths(month, year);
  }

  onMonthChange(event: CalendarMonthChangeEvent): void {
    if (event.month === undefined || event.year === undefined) {
      return;
    }
    const month = event.month - 1;
    const year = event.year;
    this.fetchCalendarEventsForTwoMonths(month, year);
  }

  /**
   * Carrega os vencimentos dos dois meses exibidos no calendario.
   *
   * Sempre repassa `selectedCompanyIds`: o backend so recorta por empresa quando recebe o
   * filtro, entao omitir a selecao faz o calendario listar vencimento de outras empresas do
   * escopo do usuario. Selecao vazia = "Todas empresas" (consolidado), que e o comportamento
   * esperado do placeholder do seletor.
   */
  fetchCalendarEventsForTwoMonths(month: number, year: number): void {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 2, 0);
    const firstDayString = this.formatDate(firstDay);
    const lastDayString = this.formatDate(lastDay);

    this.dashboardService
      .getDashboardCalendar(firstDayString, lastDayString, this.selectedCompanyIds)
      .subscribe({
        next: data => {
          this.calendarEvents = data || [];
          this.updateHighlightedDatesSet();
          this.checkPendingModalAfterDataLoad();
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.warn('Calendar API error:', error?.message || error);
          this.calendarEvents = [];
          this.updateHighlightedDatesSet();
          this.cdr.markForCheck();
        },
      });
  }

  formatDate(date: CalendarDate | Date): string {
    const year = (date as CalendarDate).year || (date as Date).getFullYear();
    const month = (
      '0' +
      ((date as CalendarDate).month >= 0
        ? (date as CalendarDate).month
        : (date as Date).getMonth() + 1)
    ).slice(-2);
    const day = ('0' + ((date as CalendarDate).day || (date as Date).getDate())).slice(-2);
    return `${year}-${month}-${day}`;
  }

  private destroyCharts(): void {
    [
      this.derivativeBarChart,
      this.derivativeBarHorizontalChart,
      this.derivativeLineChart,
      this.derivativeSemiDoughnutChart,
      this.availableVsContractedCreditChart,
    ].forEach(chart => chart?.destroy());
  }

  private updateHighlightedDatesSet() {
    try {
      const dates = this.calendarEvents
        .filter((d: CalendarDateEvent) => d.events && d.events.length > 0)
        .map((event: CalendarDateEvent) => event.date);
      this.highlightedDatesSet = new Set(dates);
    } catch (error) {
      console.warn('Error updating highlighted dates set:', error);
      this.highlightedDatesSet = new Set();
    }
  }

  isHighlighted(date: CalendarDate): boolean {
    // Proteção contra erros durante change detection
    if (!date || !this.highlightedDatesSet) return false;

    try {
      const dateString = this.formatDate({ ...date, month: date.month + 1 });
      return this.highlightedDatesSet.has(dateString);
    } catch (error) {
      return false;
    }
  }

  isSelected(date: CalendarDate): boolean {
    // Retorne false imediatamente se não há data selecionada
    if (!this.selectedDate || !date) return false;

    try {
      const dateString = this.formatDate({ ...date, month: date.month + 1 });
      const selectedString = this.formatDate(this.selectedDate);
      return dateString === selectedString;
    } catch (error) {
      return false;
    }
  }

  selectedDate?: Date;

  selectedDateEvents: CalendarEvent[] = [];
  tablePage = 0;

  private previousMonth: number = -1;
  private previousYear: number = -1;

  get calendarEventsCount(): number {
    return this.calendarEvents.reduce((count, item) => count + (item.events?.length ?? 0), 0);
  }

  get upcomingCalendarEvents(): CalendarPreviewEvent[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return this.calendarEvents
      .flatMap((item, dateIndex) => {
        const dateObject = new Date(`${item.date}T12:00:00`);
        return (item.events ?? []).map((event, eventIndex) =>
          this.buildCalendarPreviewEvent(item.date, dateObject, event, dateIndex * 100 + eventIndex)
        );
      })
      .filter(item => item.dateObject >= today)
      .sort((a, b) => a.dateObject.getTime() - b.dateObject.getTime())
      .slice(0, 4);
  }

  private buildCalendarPreviewEvent(
    date: string,
    dateObject: Date,
    event: CalendarEvent,
    index: number
  ): CalendarPreviewEvent {
    const title = this.getCalendarEventTitle(event);
    const typeLabel = this.getCalendarEventTypeLabel(event);
    const currencyCode = this.getCalendarEventCurrencyCode(event);
    const value = this.getCalendarEventValue(event);

    return {
      date,
      dateObject,
      event,
      index,
      title,
      typeLabel,
      currencyCode,
      value,
      detailRows: this.getCalendarEventDetailRows(event, dateObject, currencyCode, value),
    };
  }

  getCalendarEventTitle(event: CalendarEvent): string {
    if (event.loanOperation) {
      return this.translateReference(event.loanOperation.reference) || 'Empréstimo';
    }

    if (event.comexOperation) {
      return this.translateReference(event.comexOperation.reference) || 'Comex';
    }

    if (event.derivativeOperation) {
      return this.translateReference(event.derivativeOperation.demandReference) || 'Derivativo';
    }

    return 'Vencimento';
  }

  private getCalendarEventTypeLabel(event: CalendarEvent): string {
    if (event.loanOperation) return 'Empréstimo';
    if (event.comexOperation) return 'Comex';
    if (event.derivativeOperation) return 'Derivativo';

    return event.type || 'Vencimento';
  }

  getCalendarEventCurrencyCode(event: CalendarEvent): string {
    return (
      event.loanOperation?.currencyCode ||
      event.comexOperation?.currencyCode ||
      event.derivativeOperation?.currencyCode ||
      'BRL'
    );
  }

  getCalendarEventValue(event: CalendarEvent): number {
    return (
      event.loanOperation?.value ||
      event.comexOperation?.value ||
      event.derivativeOperation?.notionalAmount ||
      0
    );
  }

  private getCalendarEventDetailRows(
    event: CalendarEvent,
    dateObject: Date,
    currencyCode: string,
    value: number
  ): CalendarPreviewDetailRow[] {
    const earlySettled = isEarlySettledLoanEvent(event);
    const rows: CalendarPreviewDetailRow[] = [
      { label: 'Tipo', value: this.getCalendarEventTypeLabel(event) },
      // Card 3201: parcela liquidada antes do vencimento cai na data da liquidação.
      {
        label: earlySettled ? 'Liquidado em' : 'Vencimento',
        value: this.formatDateLabelFromDate(dateObject),
      },
      ...earlySettlementDetailRows(event, date => this.dateFormatter.formatDateInstant(date, true)),
    ];
    const operation = event.loanOperation || event.comexOperation || event.derivativeOperation;

    this.addCalendarDetailRow(rows, 'Empresa', operation?.companyName);
    this.addCalendarDetailRow(
      rows,
      'Status',
      this.translateCalendarOperationStatus(operation?.operationStatus)
    );
    this.addCalendarDetailRow(rows, 'ID', operation?.sequentialId);

    if (event.loanOperation || event.comexOperation) {
      const installmentOperation = event.loanOperation || event.comexOperation;

      this.addCalendarDetailRow(rows, 'Contrato', installmentOperation.contractId);
      this.addCalendarDetailRow(
        rows,
        'Parcela',
        installmentOperation.installmentNumber
          ? String(installmentOperation.installmentNumber)
          : null
      );
      this.addCalendarDetailRow(
        rows,
        'Valor parcela',
        this.formatCalendarCurrencyValue(currencyCode, installmentOperation.installmentValue)
      );
    }

    this.addCalendarDetailRow(
      rows,
      'Valor total',
      this.formatCalendarCurrencyValue(currencyCode, value)
    );

    return rows;
  }

  private addCalendarDetailRow(
    rows: CalendarPreviewDetailRow[],
    label: string,
    value: string | null | undefined
  ): void {
    if (!value) return;

    rows.push({ label, value });
  }

  private formatCalendarCurrencyValue(
    currencyCode: string,
    value: number | null | undefined
  ): string | null {
    if (value === null || value === undefined || !Number.isFinite(value)) return null;

    return `${currencyCode} ${this.formatLocalDecimal(value, 2)}`;
  }

  private formatDateLabelFromDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }

  private translateCalendarOperationStatus(status: string | null | undefined): string | null {
    if (!status) return null;

    const normalizedStatus = status.trim();
    const statusLabels: Record<string, string> = {
      Hired: 'Contratado',
      Quoted: 'Cotado',
      Simulated: 'Simulado',
      Draft: 'Rascunho',
      Active: 'Ativo',
      Open: 'Aberto',
      Closed: 'Fechado',
      Settled: 'Liquidado',
      Canceled: 'Cancelado',
      Cancelled: 'Cancelado',
      Expired: 'Expirado',
      Pending: 'Pendente',
    };

    return statusLabels[normalizedStatus] ?? normalizedStatus;
  }

  onSelectDate(selectedDate: Date | null) {
    if (!selectedDate) return;

    this.selectedDate = selectedDate;

    // Check if month changed and update calendar events
    const month = selectedDate.getMonth();
    const year = selectedDate.getFullYear();
    const currentMonth = this.currentDate.getMonth();
    const currentYear = this.currentDate.getFullYear();

    if (month !== currentMonth || year !== currentYear) {
      this.currentDate = selectedDate;
      this.fetchCalendarEventsForTwoMonths(month, year);
    }

    // Detect month change when navigating through calendar
    if (this.previousMonth !== -1 && this.previousYear !== -1) {
      if (month !== this.previousMonth || year !== this.previousYear) {
        this.fetchCalendarEventsForTwoMonths(month, year);
      }
    }
    this.previousMonth = month;
    this.previousYear = year;

    this.selectedDateEvents = this.calendarEvents
      .filter((d: CalendarDateEvent) =>
        this.dateFormatter.datesMatchIgnoringTime(d.date, selectedDate)
      )
      .flatMap((x: CalendarDateEvent) =>
        x.events.map((event: CalendarEvent) => ({
          ...event,
          type: event.type === 'derivative' ? this.t(this.tk.General.Derivative) : event.type,
        }))
      );
    this.tablePage = 0;

    this.openCalendarEventsModalWithUrl(selectedDate);
  }

  private openCalendarEventsModalWithUrl(selectedDate: Date): void {
    const queryParams = { ...this.route.snapshot.queryParams };
    queryParams['modal'] = 'calendar-events';
    queryParams['duedate'] = this.dateFormatter.formatDateToYYYYMMDD(selectedDate);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'replace' as QueryParamsHandling,
    });

    this.calendarEventsDialog
      ?.confirm(
        '',
        this.t(this.tk.Management.NdfQuotationDateHourConfirmation),
        '',
        '',
        '',
        'date-events',
        false,
        false
      )
      .then(() => {
        this.removeModalFromUrl();
      })
      .catch(() => {
        this.removeModalFromUrl();
      });
  }

  private removeModalFromUrl(): void {
    const queryParams = { ...this.route.snapshot.queryParams };
    delete queryParams['modal'];
    delete queryParams['duedate'];

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'replace' as QueryParamsHandling,
    });
  }

  private checkAndOpenModalFromUrl(params: Record<string, string | null>): void {
    // Se está abrindo por clique direto, ignorar mudança de URL
    if (this.isOpeningModalFromClick) {
      return;
    }

    if (params['modal'] === 'calendar-events' && params['duedate']) {
      // Criar a data usando o formato ISO para garantir parsing correto
      const dateString = params['duedate'];
      if (dateString) {
        // Adicionar hora para evitar problemas de timezone: YYYY-MM-DD -> YYYY-MM-DDTHH:mm:ss
        const selectedDate = new Date(dateString + 'T12:00:00');

        if (!isNaN(selectedDate.getTime())) {
          // Se os eventos do calendário já foram carregados, abrir o modal imediatamente
          if (this.calendarEvents && this.calendarEvents.length > 0) {
            this.tryOpenModalForDate(selectedDate);
          } else {
            // Se não, guardar os parâmetros para abrir depois que os dados carregarem
            this.pendingModalParams = {
              modal: params['modal'],
              date: params['duedate'],
            };
          }
        }
      }
    } else {
      // Limpar parâmetros pendentes se não há modal na URL
      this.pendingModalParams = null;
    }
  }

  private checkPendingModalAfterDataLoad(): void {
    if (this.pendingModalParams && this.pendingModalParams.modal === 'calendar-events') {
      // Criar a data usando o formato ISO para garantir parsing correto
      const dateString = this.pendingModalParams.date;
      const selectedDate = new Date(dateString + 'T12:00:00');

      if (!isNaN(selectedDate.getTime())) {
        this.tryOpenModalForDate(selectedDate);
      }
      this.pendingModalParams = null;
    }
  }

  private tryOpenModalForDate(selectedDate: Date): void {
    // Converter a data selecionada para string YYYY-MM-DD para comparação
    const selectedDateString = this.dateFormatter.formatDateToYYYYMMDD(selectedDate);

    // Preparar os eventos para a data selecionada
    this.selectedDate = selectedDate;
    this.selectedDateEvents = this.calendarEvents
      .filter((d: CalendarDateEvent) => {
        // Comparar usando strings para evitar problemas de timezone
        const eventDateString = d.date; // já vem como string YYYY-MM-DD
        return eventDateString === selectedDateString;
      })
      .flatMap((x: CalendarDateEvent) =>
        x.events.map((event: CalendarEvent) => ({
          ...event,
          type: event.type === 'derivative' ? this.t(this.tk.General.Derivative) : event.type,
        }))
      );
    this.tablePage = 0;

    this.calendarEventsDialog
      ?.confirm(
        '',
        this.t(this.tk.Management.NdfQuotationDateHourConfirmation),
        '',
        '',
        '',
        'date-events',
        false,
        false
      )
      .then(() => {
        this.removeModalFromUrl();
      })
      .catch(() => {
        this.removeModalFromUrl();
      });
  }

  get formatedEconomicCalendarList(): EconomicCalendar[] {
    const formated = this.economicCalendarList.map((calendar: EconomicCalendar) => {
      return calendar;
    });
    return formated;
  }

  /**
   * Traduz o mês do inglês para português brasileiro
   * @param monthName Nome do mês em inglês
   * @param year Ano (opcional)
   * @returns Mês/Ano em português brasileiro
   */
  private translateMonthToPortuguese(monthName: string, year?: number): string {
    const monthTranslations: { [key: string]: string } = {
      // Nomes completos em inglês
      January: 'Jan',
      February: 'Fev',
      March: 'Mar',
      April: 'Abr',
      May: 'Mai',
      June: 'Jun',
      July: 'Jul',
      August: 'Ago',
      September: 'Set',
      October: 'Out',
      November: 'Nov',
      December: 'Dez',
      // Abreviações em inglês (que vêm do backend)
      Jan: 'Jan',
      Feb: 'Fev',
      Mar: 'Mar',
      Apr: 'Abr',
      Jun: 'Jun',
      Jul: 'Jul',
      Aug: 'Ago',
      Sep: 'Set',
      Oct: 'Out',
      Nov: 'Nov',
      Dec: 'Dez',
    };

    const translatedMonth = monthTranslations[monthName] || monthName;
    return year ? `${translatedMonth}/${year}` : translatedMonth;
  }

  /**
   * Traduz referências específicas do calendário de vencimentos
   * @param reference Referência original
   * @returns Referência traduzida
   */
  translateReference(reference: string): string {
    if (!reference) return reference;

    // Verificar se contém "WORKING_CAPITAL" (pode vir com texto adicional)
    if (reference.includes('WORKING_CAPITAL')) {
      return reference.replace('WORKING_CAPITAL', 'Capital de Giro');
    }

    return reference;
  }

  getCompanyOptions(): void {
    // Sem permissão de listar empresas não há filtro de empresa na tela (ver o
    // `*appHasPermission` do cabeçalho), então buscar a lista só renderia um 403 e um
    // `console.warn` — e o card 2901 pede a Dashboard do parceiro sem erro de console.
    this.permissionsService
      .hasPermission(Permissions.Company.LIST)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(pode => {
        if (pode) this.loadCompanyOptions();
      });
  }

  private loadCompanyOptions(): void {
    this.companyService
      .getAllCompanys()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.companiesOptions =
            response?.data
              .filter(company => company.id)
              .map(company => ({
                label: company.displayName || '',
                value: company.id as string,
              })) || [];

          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.warn('Error loading companies:', error?.message || error);
          this.companiesOptions = [];
          this.cdr.markForCheck();
        },
      });
  }

  handleCompanyChange(): void {
    this.reloadCompanyScopedBlocks();
  }

  private reloadCompanyScopedBlocks(): void {
    if (!this.isBrowser) return;
    try {
      this.destroyCharts();
      this.calendarEvents = [];
      this.updateHighlightedDatesSet();

      // Recarrega eventos e gráficos de derivativos para a empresa selecionada
      const today = this.currentDate;
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();

      this.fetchCalendarEventsForTwoMonths(currentMonth, currentYear);
      if (this.showDerivativeBarChart) {
        this.initBarChart(this.selectedCompanyId);
      }
      // Gráficos temporariamente desabilitados para estabilidade
      // this.initHorizontalBarChart(this.selectedCompanyId);
      // this.initLineChart(this.selectedCompanyId);
      this.getAvailableVsContractedCredit();
    } catch (error) {
      console.warn('Error handling company change:', error);
    }
  }

  getAvailableVsContractedCredit() {
    this.dashboardService
      .getAvailableVsContractedCredit(this.selectedCompanyId || undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          this.availableVsContractedCredit = data;
          if (this.isBrowser) {
            this.prepareAvailableVsContractedCreditChart();
          }
          this.cdr.markForCheck();
        },
        error: error => {
          console.error(error);
        },
      });
  }

  prepareAvailableVsContractedCreditChart() {
    if (!this.isBrowser) return;
    if (!this.availableVsContractedCredit) {
      return;
    }

    const ctx = document.getElementById(
      'available-vs-contracted-credit-chart'
    ) as HTMLCanvasElement;
    if (!ctx) return;

    if (this.availableVsContractedCreditChart) {
      this.availableVsContractedCreditChart.destroy();
    }

    const total = this.availableVsContractedCredit.totalLimit || 0;
    const contracted = this.availableVsContractedCredit.contractedCredit || 0;

    // Se não há limite cadastrado E não há operações
    if (total === 0 && contracted === 0) {
      const config: ChartConfiguration<'doughnut'> = {
        type: 'doughnut',
        data: {
          labels: ['Sem dados'],
          datasets: [
            {
              data: [1],
              backgroundColor: ['#D0D5DD'],
              hoverBackgroundColor: ['#D0D5DD'],
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '70%',
          plugins: {
            tooltip: {
              enabled: false,
            },
            legend: {
              display: true,
              position: 'bottom',
              align: 'center',
              labels: {
                padding: 15,
                boxWidth: 12,
                boxHeight: 12,
                font: {
                  size: 12,
                  family: "'Inter', sans-serif",
                },
                color: '#6C757D',
                usePointStyle: true,
                pointStyle: 'circle',
              },
            },
          },
          layout: {
            padding: {
              bottom: 10,
            },
          },
        },
      };

      this.availableVsContractedCreditChart = new Chart(ctx, config);
      return;
    }

    // Se não há limite cadastrado ou o limite é menor que o contratado
    if (total === 0 || total < contracted) {
      const config: ChartConfiguration<'doughnut'> = {
        type: 'doughnut',
        data: {
          labels: ['Contratado (100%)'],
          datasets: [
            {
              data: [contracted],
              backgroundColor: ['#8A70D6'],
              hoverBackgroundColor: ['#8A70D6'],
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '70%',
          plugins: {
            tooltip: {
              callbacks: {
                label: context => {
                  const value = context.raw as number;
                  return `${context.label}: ${value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`;
                },
              },
            },
            legend: {
              display: true,
              position: 'bottom',
              align: 'center',
              labels: {
                padding: 15,
                boxWidth: 12,
                boxHeight: 12,
                font: {
                  size: 12,
                  family: "'Inter', sans-serif",
                },
                color: '#6C757D',
                usePointStyle: true,
                pointStyle: 'circle',
              },
            },
          },
          layout: {
            padding: {
              bottom: 10,
            },
          },
        },
      };

      this.availableVsContractedCreditChart = new Chart(ctx, config);
      return;
    }

    // Caso normal: há limite e ele é maior que o contratado
    const available = this.availableVsContractedCredit.availableCredit;
    const availablePercentage = ((available / total) * 100).toFixed(1);
    const contractedPercentage = ((contracted / total) * 100).toFixed(1);

    const config: ChartConfiguration<'doughnut'> = {
      type: 'doughnut',
      data: {
        labels: [`Disponível (${availablePercentage}%)`, `Contratado (${contractedPercentage}%)`],
        datasets: [
          {
            data: [available, contracted],
            backgroundColor: ['#47DCFD', '#8A70D6'],
            hoverBackgroundColor: ['#47DCFD', '#8A70D6'],
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '70%',
        plugins: {
          tooltip: {
            callbacks: {
              label: context => {
                const value = context.raw as number;
                return `${context.label}: ${value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`;
              },
            },
          },
          legend: {
            display: true,
            position: 'bottom',
            align: 'center',
            labels: {
              padding: 15,
              boxWidth: 12,
              boxHeight: 12,
              font: {
                size: 12,
                family: "'Inter', sans-serif",
              },
              color: '#6C757D',
              usePointStyle: true,
              pointStyle: 'circle',
            },
          },
        },
        layout: {
          padding: {
            bottom: 10,
          },
        },
      },
    };

    this.availableVsContractedCreditChart = new Chart(ctx, config);
  }

  // Helper methods for KPI cards
  getCurrencyByCode(code: string): CurrencyExchangeCurrent | undefined {
    return this.currencyExchangeCurrent.find(currency => currency?.currency?.includes(code));
  }

  private getKeyIndicatorsForFixedSelector(): KeyIndicatorResponse[] {
    const indicators = this.keyIndicatorsDashboard?.indicators ?? [];
    const copomIndicators = this.getCopomFixedIndicators(
      this.keyIndicatorsDashboard?.copom ?? null
    );
    const focusIndicators = this.getFocusFixedIndicators(
      this.keyIndicatorsDashboard?.focus ?? null
    );
    const extendedIndicators = this.getExtendedFixedIndicators(
      this.keyIndicatorsDashboard?.extended ?? null
    );
    const copomCodes = new Set(copomIndicators.map(indicator => indicator.code));
    const focusCodes = new Set(focusIndicators.map(indicator => indicator.code));
    const extendedCodes = new Set(extendedIndicators.map(indicator => indicator.code));

    return [
      ...indicators.filter(
        indicator =>
          !copomCodes.has(indicator.code) &&
          !focusCodes.has(indicator.code) &&
          !extendedCodes.has(indicator.code) &&
          !this.isLegacyFocusIndicatorDuplicated(indicator.code, focusIndicators)
      ),
      ...copomIndicators,
      ...focusIndicators,
      ...extendedIndicators,
    ];
  }

  private getCopomFixedIndicators(copom: CopomCalendarResponse | null): KeyIndicatorResponse[] {
    if (!copom) {
      return [];
    }

    return [
      this.createCopomFixedIndicator(
        copom,
        copom.lastMinutes,
        'copom-last-minutes',
        'Última ata do Copom',
        'Última ata do Copom publicada pelo Banco Central do Brasil.',
        copom.lastMinutes?.meetingNumber
          ? `Reunião ${copom.lastMinutes.meetingNumber}`
          : 'Publicada'
      ),
      this.createCopomFixedIndicator(
        copom,
        copom.lastStatement,
        'copom-last-statement',
        'Último comunicado do Copom',
        'Último comunicado do Copom publicado pelo Banco Central do Brasil.',
        copom.lastStatement?.meetingNumber
          ? `Reunião ${copom.lastStatement.meetingNumber}`
          : 'Publicado'
      ),
      this.createCopomFixedIndicator(
        copom,
        copom.nextMeetingsInYear?.[0] ?? null,
        'copom-next-meeting',
        'Próxima reunião do Copom',
        'Próxima reunião do Copom no calendário oficial do Banco Central.',
        this.formatCopomDateLabel(copom.nextMeetingsInYear?.[0]?.date)
      ),
      this.createCopomFixedIndicator(
        copom,
        copom.nextMinutesAndStatementsInYear?.[0] ?? null,
        'copom-next-publication',
        'Próxima publicação do Copom',
        'Próxima publicação oficial de ata ou comunicado do Copom.',
        this.formatCopomDateLabel(copom.nextMinutesAndStatementsInYear?.[0]?.date)
      ),
    ].filter((indicator): indicator is KeyIndicatorResponse => !!indicator);
  }

  private createCopomFixedIndicator(
    copom: CopomCalendarResponse,
    event: CopomEventResponse | null | undefined,
    code: string,
    name: string,
    description: string,
    formattedValue: string | null
  ): KeyIndicatorResponse | null {
    if (!event) {
      return null;
    }

    return {
      code,
      name,
      description,
      value: null,
      formattedValue,
      unit: null,
      period: this.formatCopomDateLabel(event.date),
      source: event.source,
      apiUrl: event.apiUrl,
      officialPageUrl: event.officialPageUrl,
      lastSuccessfulUpdateAtGmt: copom.lastSuccessfulUpdateAtGmt,
      status: copom.status,
      errorMessage: copom.errorMessage,
    };
  }

  private getFocusFixedIndicators(focus: FocusReportResponse | null): KeyIndicatorResponse[] {
    if (!focus) {
      return [];
    }

    const expectationIndicators = (focus.expectations ?? [])
      .map(expectation => this.createFocusExpectationFixedIndicator(focus, expectation))
      .filter((indicator): indicator is KeyIndicatorResponse => !!indicator);
    const lastRelease = this.createFocusCalendarFixedIndicator(
      focus,
      focus.pastReleasesInYear?.at(-1) ?? null,
      focus.lastReleaseDate,
      'focus-last-release',
      'Última divulgação Focus',
      'Última divulgação oficial do Boletim Focus no calendário do Banco Central.'
    );
    const nextRelease = this.createFocusReleaseFixedIndicator(
      focus,
      focus.nextReleasesInYear?.[0] ?? null,
      focus.nextReleaseDate,
      'focus-next-release',
      'Próxima divulgação Focus',
      'Próxima divulgação oficial do Boletim Focus no calendário do Banco Central.'
    );

    return [...expectationIndicators, lastRelease, nextRelease].filter(
      (indicator): indicator is KeyIndicatorResponse => !!indicator
    );
  }

  private getExtendedFixedIndicators(
    extended: ExtendedFinancialIndicatorsResponse | null
  ): KeyIndicatorResponse[] {
    if (!extended) {
      return [];
    }

    const focusExchangeRateIndicators = (extended.focusExchangeRate ?? []).map(expectation =>
      this.createExtendedFocusExchangeRateFixedIndicator(extended, expectation)
    );

    return [
      ...(extended.bcbAndIpeadata ?? []),
      ...(extended.ibge ?? []),
      ...(extended.ipcaByGroup ?? []),
      ...(extended.industrialProductionByCategory ?? []),
      ...focusExchangeRateIndicators,
    ];
  }

  private createExtendedFocusExchangeRateFixedIndicator(
    extended: ExtendedFinancialIndicatorsResponse,
    expectation: FocusExpectationResponse
  ): KeyIndicatorResponse {
    const collectionDate =
      this.formatLocalDateLabel(expectation.collectionDate) ?? expectation.collectionDate;
    const releaseDate =
      this.formatLocalDateLabel(expectation.releaseDate) ?? expectation.releaseDate;

    return {
      code: `focus-exchange-rate-${expectation.referenceYear}`,
      name: `Focus Câmbio ${expectation.referenceYear}`,
      description: `Mediana da expectativa de câmbio Focus para ${expectation.referenceYear}.`,
      value: expectation.value,
      formattedValue: expectation.formattedValue,
      unit: null,
      period: expectation.referenceYear,
      source: expectation.source,
      apiUrl: expectation.apiUrl,
      officialPageUrl: expectation.officialPageUrl,
      lastSuccessfulUpdateAtGmt: expectation.releaseDate ?? extended.lastSuccessfulUpdateAtGmt,
      status: extended.status,
      errorMessage: extended.errorMessage,
      metadata: [
        { label: 'Ano referência', value: expectation.referenceYear },
        ...(collectionDate ? [{ label: 'Data da pesquisa', value: collectionDate }] : []),
        ...(releaseDate ? [{ label: 'Divulgado em', value: releaseDate }] : []),
        ...(expectation.respondents
          ? [{ label: 'Respondentes', value: String(expectation.respondents) }]
          : []),
      ],
    };
  }

  private createFocusExpectationFixedIndicator(
    focus: FocusReportResponse,
    expectation: FocusExpectationResponse
  ): KeyIndicatorResponse | null {
    const code = this.getFocusExpectationCode(expectation.indicator);
    if (!code) {
      return null;
    }

    const collectionDate =
      this.formatLocalDateLabel(expectation.collectionDate) ?? expectation.collectionDate;
    const releaseDate =
      this.formatLocalDateLabel(expectation.releaseDate) ?? expectation.releaseDate;

    return {
      code,
      name: this.getKeyIndicatorDisplayName({
        code,
        name: `Focus ${expectation.indicator}`,
      } as KeyIndicatorResponse),
      description: `Mediana das expectativas de mercado para ${expectation.indicator} no ano de ${expectation.referenceYear}.`,
      value: expectation.value,
      formattedValue: expectation.formattedValue,
      unit: null,
      period: expectation.referenceYear,
      source: expectation.source,
      apiUrl: expectation.apiUrl,
      officialPageUrl: expectation.officialPageUrl,
      lastSuccessfulUpdateAtGmt: expectation.releaseDate ?? focus.lastSuccessfulUpdateAtGmt,
      status: focus.status,
      errorMessage: focus.errorMessage,
      metadata: [
        { label: 'Ano referência', value: expectation.referenceYear },
        ...(collectionDate ? [{ label: 'Data da pesquisa', value: collectionDate }] : []),
        ...(releaseDate ? [{ label: 'Divulgado em', value: releaseDate }] : []),
      ],
    };
  }

  private createFocusReleaseFixedIndicator(
    focus: FocusReportResponse,
    event: CopomEventResponse | null | undefined,
    releaseDate: string | null | undefined,
    code: string,
    name: string,
    description: string
  ): KeyIndicatorResponse | null {
    return this.createFocusCalendarFixedIndicator(
      focus,
      event,
      releaseDate,
      code,
      name,
      description
    );
  }

  private createFocusCalendarFixedIndicator(
    focus: FocusReportResponse,
    event: CopomEventResponse | null | undefined,
    releaseDate: string | null | undefined,
    code: string,
    name: string,
    description: string
  ): KeyIndicatorResponse | null {
    const date = releaseDate ?? event?.date;
    const formattedDate = this.formatCopomDateLabel(date);
    if (!formattedDate) {
      return null;
    }

    return {
      code,
      name,
      description,
      value: null,
      formattedValue: formattedDate,
      unit: null,
      period: focus.referenceYear,
      source: event?.source ?? 'Banco Central do Brasil/Calendário do BC',
      apiUrl: event?.apiUrl ?? null,
      officialPageUrl: event?.officialPageUrl ?? null,
      lastSuccessfulUpdateAtGmt: focus.lastSuccessfulUpdateAtGmt ?? releaseDate ?? null,
      status: focus.status,
      errorMessage: focus.errorMessage,
      metadata: [
        {
          label: code === 'focus-next-release' ? 'Próxima divulgação' : 'Divulgado em',
          value: formattedDate,
        },
      ],
    };
  }

  private getFocusExpectationCode(indicator: string | null | undefined): string | null {
    const normalized = (indicator ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

    if (normalized === 'ipca') return 'focus-expectation-ipca';
    if (normalized === 'cambio') return 'focus-expectation-cambio';
    if (normalized === 'pib') return 'focus-expectation-pib';
    if (normalized === 'selic') return 'focus-expectation-selic';

    return null;
  }

  private isLegacyFocusIndicatorDuplicated(
    code: string,
    focusIndicators: KeyIndicatorResponse[]
  ): boolean {
    if (!focusIndicators.length) {
      return false;
    }

    return code === 'focus-ipca-current-year' || code === 'focus-selic-current-year';
  }

  private toKeyIndicatorFixedId(code: string): string {
    return `key:${code}`;
  }

  private getPtaxFixedId(currencyCode: string, quoteType: 'sale' | 'purchase'): string {
    return quoteType === 'purchase' ? `ptax-buy:${currencyCode}` : `ptax:${currencyCode}`;
  }

  private createLocalKeyIndicator(indicator: {
    code: string;
    name: string;
    description: string;
    value: number | null;
    formattedValue: string | null;
    unit: string | null;
    period: string | null;
    source: string;
    lastSuccessfulUpdateAtGmt: string | null;
  }): KeyIndicatorResponse {
    return {
      ...indicator,
      apiUrl: null,
      officialPageUrl: null,
      status: 'Updated',
      errorMessage: null,
    };
  }

  private formatLocalPercent(value: number | null | undefined, fractionDigits = 2): string {
    return `${this.formatLocalDecimal(value ?? 0, fractionDigits)}%`;
  }

  private formatLocalDecimal(value: number | null | undefined, fractionDigits = 2): string {
    return (value ?? 0).toLocaleString('pt-BR', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  }

  private formatLocalDateLabel(value: string | null | undefined): string | null {
    if (!value) return null;

    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-');
    if (!year || !month || !day) return value;

    const timeLabel = timePart?.slice(0, 5);
    return timeLabel ? `${day}/${month}/${year} ${timeLabel}` : `${day}/${month}/${year}`;
  }

  private formatCopomDateLabel(value: string | null | undefined): string | null {
    if (!value) return null;

    if (value.includes(' a ')) {
      return value
        .split(' a ')
        .map(item => this.formatCopomDateLabel(item) ?? item)
        .join(' a ');
    }

    return this.formatLocalDateLabel(value);
  }

  private saveFixedIndicatorPreferences(): void {
    this.indicatorPreferencesRepository.saveFixedIndicatorIds(this.fixedIndicatorIds);
  }

  private ensureFixedIndicatorsExist(): void {
    const availableIds = new Set(this.fixedIndicatorOptions.map(option => option.id));
    const validSelectedIds = this.fixedIndicatorIds.filter(id => availableIds.has(id));

    if (validSelectedIds.length >= 4) {
      this.fixedIndicatorIds = validSelectedIds.slice(0, 8);
      return;
    }

    this.fixedIndicatorIds = [...DEFAULT_FIXED_DASHBOARD_INDICATORS];
  }

  private normalizeFixedIndicatorSearch(value: string | null | undefined): string {
    return (value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private normalizeCurrencyCode(currencyCode: string | undefined): string | null {
    if (!currencyCode) return null;
    const upper = currencyCode.toUpperCase();
    // Handles formats like: "USD", "USD/BRL", "USD-BRL", "USDBRL", etc.
    const match = upper.match(/[A-Z]{3}/);
    return match?.[0] ?? null;
  }

  isYuanCurrency(currencyCode: string | undefined): boolean {
    const code = this.normalizeCurrencyCode(currencyCode);
    return code === 'CNY' || code === 'CNH';
  }

  getCurrencyIcon(currencyCode: string | undefined): string {
    const code = this.normalizeCurrencyCode(currencyCode);
    if (!code) return 'pi pi-dollar';
    if (code === 'USD') return 'pi pi-dollar';
    if (code === 'EUR') return 'pi pi-euro';
    // PrimeIcons (project version) doesn't include pi-yen; use money-bill for CNY/CNH/JPY.
    if (code === 'CNY' || code === 'CNH') return 'pi pi-money-bill';
    if (code === 'JPY') return 'pi pi-money-bill';
    // Keep dollar as fallback for other currencies
    return 'pi pi-dollar';
  }

  getCurrencyIconClass(currencyCode: string | undefined, index: number): string {
    const code = this.normalizeCurrencyCode(currencyCode);
    if (!code) return 'bg-icon-blue';
    if (code === 'USD') return 'bg-icon-blue';
    if (code === 'EUR') return 'bg-icon-cyan';
    if (code === 'CNY' || code === 'CNH') return 'bg-icon-orange';
    if (code === 'JPY') return 'bg-icon-cyan';
    // Para outras moedas, alterna entre azul e ciano baseado no índice
    return index % 2 === 0 ? 'bg-icon-blue' : 'bg-icon-cyan';
  }

  getCurrencyMainValue(quote: number | undefined): string {
    if (!quote) return '0,00';
    return quote.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  getCurrencyDecimalValue(quote: number | undefined): string {
    if (!quote) return '00';
    const formatted = quote.toLocaleString('pt-BR', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });
    // Pega os últimos 2 dígitos após a vírgula
    const parts = formatted.split(',');
    if (parts.length > 1) {
      return parts[1].slice(-2);
    }
    return '00';
  }

  getVariationBadgeClass(variation: number): string {
    return variation >= 0 ? 'badge-success' : 'badge-danger';
  }

  getVariationIconClass(variation: number): string {
    return variation >= 0 ? 'pi-arrow-up' : 'pi-arrow-down';
  }

  getYearVariationBadgeClass(variation: number): string {
    return variation >= 0 ? 'badge-success' : 'badge-danger';
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyCharts();
  }
}
